import '../styles/pages/asistencia-equipos.css';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../components/contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Pagination from '../components/common/Pagination';
import EntityModal from '../components/common/EntityModal';
import { useToast } from '../components/contexts/ToastContext';
import apiService from '../services/apiService';
import usePagination from '../hooks/usePagination';
import { getTodayUTC, esFechaEditableUTC } from '../../../shared/utils/dateUtils';
import * as XLSX from 'xlsx';
import { downloadFile } from '../utils/downloadHelpers';
import { validateDate, validateDateRange } from '../utils/sanitization';
import Breadcrumb from '../components/common/Breadcrumb';
import ROUTES from '../constants/routes';

/**
 * Badge de estado de asistencia por equipo.
 * Diferencia entre cambio pendiente de guardar, ya registrado y sin registrar.
 *
 * @param {Object}  props
 * @param {boolean} props.tieneAsistencia - Si el equipo tiene asistencia registrada en BD
 * @param {boolean} props.isPending       - Si hay un cambio pendiente de guardar
 * @returns {JSX.Element}
 */
function AsistenciaBadge({ tieneAsistencia, isPending }) {
  if (isPending) return (
    <span className="badge badge-secondary">
      <i className="fas fa-minus"></i> Pendiente
    </span>
  );
  if (tieneAsistencia) return (
    <span className="badge badge-success">
      <i className="fas fa-check"></i> Registrado
    </span>
  );
  return (
    <span className="badge badge-secondary">
      <i className="fas fa-minus"></i> Pendiente
    </span>
  );
}

/**
 * Checkbox de selección reutilizable para tabla y cards.
 *
 * @param {Object}      props
 * @param {number}      props.equipoId    - ID del equipo
 * @param {Set<number>} props.selectedIds - Set de IDs seleccionados
 * @param {Function}    props.onToggle    - Callback al cambiar selección
 * @returns {JSX.Element}
 */
function SelectCheckbox({ equipoId, selectedIds, onToggle }) {
  return (
    <input type="checkbox" className="form-checkbox"
           checked={selectedIds.has(equipoId)}
           onChange={() => onToggle(equipoId)} />
  );
}

const EXPORT_FIELDS = [
  { name: 'fecha_inicio', label: 'Fecha inicio', type: 'date', required: true,
    validators: [(v) => validateDate(v, 'Fecha inicio')] },
  { name: 'fecha_fin',    label: 'Fecha fin',    type: 'date', required: true,
    validators: [(v) => validateDate(v, 'Fecha fin')] }
];

/**
 * Página de registro diario de asistencia/ubicación de equipos.
 *
 * Permite registro individual (pendingChanges + botón guardar por fila) y masivo.
 * Las asistencias no se eliminan, solo se editan.
 *
 * @returns {JSX.Element}
 */
function AsistenciaEquipos() {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();

  // Estado de datos
  const [equipos, setEquipos]                       = useState([]);
  const [ubicaciones, setUbicaciones]               = useState([]);
  const [asistenciasHoy, setAsistenciasHoy]         = useState({});
  const [loading, setLoading]                       = useState(true);
  const [resumenUbicaciones, setResumenUbicaciones] = useState([]);

  // Estado de filtros
  const [fecha, setFecha]                       = useState(getTodayUTC());
  const puedeEditar                             = esFechaEditableUTC(fecha);
  const [soloSinRegistrar, setSoloSinRegistrar] = useState(false);

  // Estado de selección masiva
  const [selectedIds, setSelectedIds]           = useState(new Set());
  const [ubicacionMasiva, setUbicacionMasiva]   = useState('');
  const [processingMasivo, setProcessingMasivo] = useState(false);

  // Estado de pendingChanges y UI
  const [pendingChanges, setPendingChanges] = useState({}); // { [equipoId]: number }
  const [sidebarOpen, setSidebarOpen]       = useState(true);
  const [savingIds, setSavingIds]           = useState(new Set());
  const fetchSeqRef                         = useRef(0);

  // Estado de filtro por faena (client-side sobre asistenciasHoy)
  const [filtroUbicacionId, setFiltroUbicacionId] = useState(null);

  // Estado del modal de exportación
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportLoading, setExportLoading]     = useState(false);

  /**
   * Carga ubicaciones para dropdown (solo una vez al montar)
   */
  useEffect(() => {
    /**
     * Obtiene lista de ubicaciones activas para el dropdown de selección
     * @returns {Promise<void>}
     */
    const fetchUbicaciones = async () => {
      try {
        const response = await apiService.getUbicacionesLista();
        if (response.success) setUbicaciones(response.data || []);
      } catch (error) {
        showToast('Error al cargar ubicaciones', 'error');
      }
    };
    fetchUbicaciones();
  }, [showToast]);

  /**
   * Carga equipos según modo de filtro.
   * Guard de secuencia evita race conditions al cambiar fecha rápidamente.
   * NO limpia pendingChanges — las claves se borran individualmente al guardar.
   */
  const fetchData = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      if (soloSinRegistrar) {
        const response = await apiService.getEquiposSinAsistencia(fecha, { limit: 1000 });
        if (seq !== fetchSeqRef.current) return;
        if (response.success) {
          setEquipos(response.data || []);
          setAsistenciasHoy({});
          setResumenUbicaciones([]);
        }
      } else {
        const equiposRes = await apiService.getEquipos({ limit: 1000, activo: 1 });
        if (seq !== fetchSeqRef.current) return;
        if (equiposRes.success) {
          const equiposList = equiposRes.data || [];
          setEquipos(equiposList);

          if (equiposList.length > 0) {
            const asistenciasRes = await apiService.getAsistenciasEquipo({ fecha, limit: 1000 });
            const asistenciasMap = {};
            if (asistenciasRes.success && asistenciasRes.data) {
              asistenciasRes.data.forEach(asist => {
                asistenciasMap[asist.equipo_id] = asist;
              });
            }
            setAsistenciasHoy(asistenciasMap);
          } else {
            setAsistenciasHoy({});
          }

          const resumenRes = await apiService.getAsistenciasResumenPorUbicacion(fecha);
          if (resumenRes.success) setResumenUbicaciones(resumenRes.data || []);
        }
      }
    } catch (error) {
      if (seq === fetchSeqRef.current) showToast('Error al cargar datos', 'error');
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [fecha, soloSinRegistrar, showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Estadísticas (fuente de verdad: resumenUbicaciones del backend)
  const totalEquipos     = equipos.length;
  const registrados      = resumenUbicaciones.reduce((sum, ub) => sum + ub.cantidad_equipos, 0);
  const coberturaPercent = totalEquipos ? Math.round(registrados / totalEquipos * 100) : 0;

  // Filtra desde asistenciasHoy los equipos registrados en la faena seleccionada.
  // asistenciasHoy solo contiene equipos CON asistencia registrada ese día,
  // por lo que el filtro muestra "equipos registrados en esta faena", no todos los equipos de la faena.
  // El guard `a.equipo != null` descarta filas con equipo_id NULL (defensive).
  const equiposMostrados = filtroUbicacionId
    ? Object.values(asistenciasHoy)
        .filter(a => a.ubicacion_id === filtroUbicacionId && a.equipo != null)
        .map(a => a.equipo)
    : equipos;

  const ubicacionActivaFiltro = filtroUbicacionId
    ? resumenUbicaciones.find(u => u.ubicacion_id === filtroUbicacionId)
    : null;

  // Paginación client-side sobre equiposMostrados
  const {
    page,
    limit,
    totalItems: totalPaginacion,
    paginatedData: equiposPaginados,
    handlePageChange: changePage,
    handleLimitChange: changeLimit,
    resetPage,
  } = usePagination({ mode: 'client', data: equiposMostrados });

  // Permisos (evaluados una vez antes del return)
  const canMasivo = puedeEditar && hasPermission('asistencias_equipo.masivo');
  const canCreate = puedeEditar && hasPermission('asistencias_equipo.create');

  // Columnas para colSpan (base 4: Código + Marca/Modelo + Ubicación Hoy + Estado)
  const totalColumns = 4 + (canMasivo ? 1 : 0);

  // Options de ubicación reutilizados en selects
  const ubicacionOptions = ubicaciones.map(ub => (
    <option key={ub.id} value={ub.id}>{ub.nombre}</option>
  ));

  /**
   * Obtiene estado derivado de una fila de equipo
   * @param {number} equipoId - ID del equipo
   * @returns {{ tieneAsistencia: boolean, isPending: boolean, selectValue: number|string }}
   */
  const getEquipoRowState = (equipoId) => ({
    tieneAsistencia: !!asistenciasHoy[equipoId],
    isPending:       pendingChanges[equipoId] !== undefined,
    selectValue:     pendingChanges[equipoId] ?? asistenciasHoy[equipoId]?.ubicacion_id ?? ''
  });

  /**
   * Obtiene el nombre de ubicación para mostrar en cards mobile
   * @param {number} equipoId - ID del equipo
   * @returns {string} Nombre de la ubicación o '—'
   */
  const getUbicacionDisplay = (equipoId) => {
    const asist = asistenciasHoy[equipoId];
    return asist?.ubicacion?.nombre || asist?.ubicacion_nombre || '—';
  };

  /**
   * Maneja cambio de página: resetea selección y pendingChanges
   * @param {number} newPage
   */
  const handlePageChange = (newPage) => {
    changePage(newPage);
    setSelectedIds(new Set());
    setPendingChanges({});
  };

  /**
   * Maneja cambio de items por página: vuelve a página 1 y resetea selección
   * @param {number} newLimit
   */
  const handleLimitChange = (newLimit) => {
    changeLimit(newLimit);
    setSelectedIds(new Set());
    setPendingChanges({});
  };

  /**
   * Maneja cambio de fecha: resetea contexto completo (pendingChanges, selección, filtro faena)
   * @param {string} nuevaFecha - Fecha en formato YYYY-MM-DD
   */
  const handleFechaChange = (nuevaFecha) => {
    setFecha(nuevaFecha);
    setPendingChanges({});
    setSelectedIds(new Set());
    setFiltroUbicacionId(null);
    resetPage();
  };

  /**
   * Maneja cambio de filtro "solo sin registrar"
   * @param {boolean} checked
   */
  const handleFilterChange = (checked) => {
    setSoloSinRegistrar(checked);
    resetPage();
    setSelectedIds(new Set());
    setPendingChanges({});
    setFiltroUbicacionId(null);
  };

  /**
   * Aplica o desactiva el filtro por faena (toggle)
   * @param {number} ubicacionId - ID de la ubicación a filtrar
   */
  const handleFiltrarPorFaena = (ubicacionId) => {
    setFiltroUbicacionId(prev => prev === ubicacionId ? null : ubicacionId);
    setSelectedIds(new Set());
    resetPage();
  };

  /**
   * Selecciona/deselecciona un equipo
   * @param {number} equipoId
   */
  const handleToggleSelect = (equipoId) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      s.has(equipoId) ? s.delete(equipoId) : s.add(equipoId);
      return s;
    });
  };

  /**
   * Selecciona/deselecciona todos los equipos visibles
   */
  const handleToggleAll = () => {
    if (selectedIds.size === equiposPaginados.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(equiposPaginados.map(eq => eq.id)));
    }
  };

  /**
   * Maneja cambio del select de ubicación por fila.
   * Si value es '', elimina la clave de pendingChanges para revertir al estado guardado.
   * @param {number} equipoId
   * @param {string} value
   */
  const handleSelectChange = (equipoId, value) => {
    if (value === '') {
      setPendingChanges(prev => { const n = { ...prev }; delete n[equipoId]; return n; });
    } else {
      setPendingChanges(prev => ({ ...prev, [equipoId]: parseInt(value, 10) }));
    }
  };

  /**
   * Registra asistencia individual desde pendingChanges.
   * Protege contra doble-click con savingIds.
   * @param {number} equipoId
   */
  const handleRegistroIndividual = async (equipoId) => {
    const ubicacionId = pendingChanges[equipoId];
    if (ubicacionId === undefined || ubicacionId === null || isNaN(Number(ubicacionId))) return;
    if (savingIds.has(equipoId)) return;

    setSavingIds(prev => new Set(prev).add(equipoId));
    try {
      const asistenciaExistente = asistenciasHoy[equipoId];
      if (asistenciaExistente) {
        await apiService.updateAsistenciaEquipo(asistenciaExistente.id, { ubicacion_id: ubicacionId });
      } else {
        await apiService.createAsistenciaEquipo({ equipo_id: equipoId, ubicacion_id: ubicacionId, fecha });
      }
      setPendingChanges(prev => { const n = { ...prev }; delete n[equipoId]; return n; });
      showToast('Ubicación registrada', 'success');
      fetchData();
    } catch (error) {
      showToast(error.message || 'Error al registrar ubicación', 'error');
    } finally {
      setSavingIds(prev => { const s = new Set(prev); s.delete(equipoId); return s; });
    }
  };

  /**
   * Registra asistencia masiva para todos los equipos seleccionados
   */
  /**
   * Genera y descarga un Excel con asistencias en el rango seleccionado.
   * Formato: hoja única con columnas Código, Marca, Modelo, Fecha, Cód. Ubicación, Nombre Ubicación.
   */
  const handleExportarRango = useCallback(async (formData) => {
    setExportLoading(true);
    try {
      const result = await apiService.getAsistenciasRangoExport({
        fecha_inicio: formData.fecha_inicio,
        fecha_fin: formData.fecha_fin
      });
      if (!result.success) throw new Error(result.message || 'Error al obtener datos');

      const rows = result.data.map(r => ({
        'Código': r.codigo,
        'Marca': r.marca,
        'Modelo': r.modelo,
        'Fecha': r.fecha,
        'Cód. Ubicación': r.ubicacion_codigo,
        'Nombre Ubicación': r.ubicacion_nombre
      }));

      if (rows.length === 0) {
        showToast('No hay asistencias registradas en ese rango de fechas', 'warning');
        setExportLoading(false);
        return;
      }

      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 14 }, { wch: 18 }, { wch: 35 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Asistencias');

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `asistencias_${formData.fecha_inicio}_${formData.fecha_fin}_${timestamp}.xlsx`;

      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      downloadFile(blob, filename);
      showToast(`Excel generado: ${rows.length} registros`, 'success');
      setExportModalOpen(false);
    } catch (error) {
      showToast('Error al generar el Excel. Intenta nuevamente.', 'error');
    } finally {
      setExportLoading(false);
    }
  }, [showToast]);

  const handleRegistroMasivo = async () => {
    if (selectedIds.size === 0) { showToast('Seleccione al menos un equipo', 'warning'); return; }
    if (!ubicacionMasiva)       { showToast('Seleccione una ubicación', 'warning'); return; }

    setProcessingMasivo(true);
    try {
      const response = await apiService.createAsistenciaMasiva({
        equipo_ids: Array.from(selectedIds),
        ubicacion_id: parseInt(ubicacionMasiva),
        fecha: fecha
      });
      if (response.success) {
        const { created, updated, errors } = response.data;
        showToast(`Registrados: ${created} nuevos, ${updated} actualizados`, errors?.length ? 'warning' : 'success');
        setSelectedIds(new Set());
        setUbicacionMasiva('');
        setPendingChanges({});
        fetchData();
      }
    } catch (error) {
      showToast(error.message || 'Error en registro masivo', 'error');
    } finally {
      setProcessingMasivo(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="main-content">

        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Asistencia' }
        ]} />

        {/* ── Panel de control ─────────────────────────────────────── */}
        <div className="card asist-panel margin-bottom-md">
          <div className="asist-control-panel">

            {/* Fecha + Filtro: misma fila en mobile */}
            <div className="asist-cp-row">
              <div className="asist-cp-section">
                <input type="date" className="form-control asist-cp-date" value={fecha}
                       onChange={(e) => handleFechaChange(e.target.value)} />
              </div>
              {puedeEditar ? (
                <div className="asist-cp-section">
                  <label className="form-check">
                    <input type="checkbox" id="filtro-sin-registrar" className="form-checkbox"
                           checked={soloSinRegistrar}
                           onChange={(e) => handleFilterChange(e.target.checked)} />
                    <span className="form-check-label">Solo sin registrar</span>
                  </label>
                </div>
              ) : (
                <div className="asist-cp-section">
                  <span className="badge badge-warning"><i className="fas fa-lock"></i> Solo lectura</span>
                </div>
              )}
            </div>

            {/* Progreso */}
            {!soloSinRegistrar && totalEquipos > 0 && (
              <div className="asist-cp-section asist-cp-section--progress">
                <div className="asist-progress-row">
                  <div className="asist-progress-track">
                    <div className="asist-progress-fill" style={{ width: `${coberturaPercent}%` }}></div>
                  </div>
                  <span className="asist-progress-text">
                    <strong>{registrados}</strong> / {totalEquipos} equipos
                  </span>
                </div>
              </div>
            )}

            {/* Exportar */}
            <div className="asist-cp-section asist-cp-section--export">
              <button className="btn btn-secondary btn-sm" onClick={() => setExportModalOpen(true)}>
                <i className="fas fa-file-excel"></i> Exportar
              </button>
            </div>

            {/* Asignar masivo */}
            {canMasivo && (
              <div className="asist-cp-section asist-cp-section--masivo">
                <div className="asist-masivo-row">
                  <select className="form-select asist-masivo-select" value={ubicacionMasiva}
                          onChange={(e) => setUbicacionMasiva(e.target.value)}>
                    <option value="">Elegir ubicación...</option>
                    {ubicacionOptions}
                  </select>
                  <button className="btn btn-primary btn-sm" onClick={handleRegistroMasivo}
                          disabled={processingMasivo || selectedIds.size === 0 || !ubicacionMasiva}>
                    {processingMasivo
                      ? <><i className="fas fa-spinner fa-spin"></i> Procesando...</>
                      : <><i className="fas fa-layer-group"></i> Marcar ({selectedIds.size})</>}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setSelectedIds(new Set())}
                          title="Desmarcar todo">
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>
            )}

          </div>

          {/* Toolbar masivo — visible cuando hay selección */}
          {selectedIds.size > 0 && (
            <div className="asist-masivo-toolbar">
              <i className="fas fa-check-square"></i>
              <span className="asist-masivo-count">
                <strong>{selectedIds.size}</strong> equipos seleccionados
              </span>
              <span className="asist-masivo-hint">— elige ubicación y presiona "Marcar"</span>
            </div>
          )}

          {/* Banner de filtro activo por faena */}
          {ubicacionActivaFiltro && (
            <button
              className="asist-filtro-banner"
              onClick={() => setFiltroUbicacionId(null)}
            >
              <span className="asist-filtro-banner-action">
                <i className="fas fa-times"></i> Quitar filtro
              </span>
              <span className="asist-filtro-banner-info">
                <i className="fas fa-map-marker-alt"></i>{' '}
                Filtrando: <strong>{ubicacionActivaFiltro.nombre}</strong>
                {' · '}{equiposMostrados.length} equipos registrados aquí
              </span>
            </button>
          )}
        </div>

        {/* ── Grid: tabla + sidebar ────────────────────────────────── */}
        <div className={`asist-content-grid${soloSinRegistrar ? ' asist-content-grid--no-sidebar' : ''}`}>

          {/* TABLA DESKTOP/TABLET + CARDS MOBILE */}
          <div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    {canMasivo && (
                      <th style={{ width: '40px', textAlign: 'center' }}>
                        <input type="checkbox" className="form-checkbox"
                               checked={equiposPaginados.length > 0 && selectedIds.size === equiposPaginados.length}
                               onChange={handleToggleAll} />
                      </th>
                    )}
                    <th>Código</th>
                    <th>Marca / Modelo</th>
                    <th>Ubicación Hoy</th>
                    <th style={{ textAlign: 'center' }}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={totalColumns} className="text-center padding-lg">
                        <i className="fas fa-spinner fa-spin"></i> Cargando...
                      </td>
                    </tr>
                  ) : equiposMostrados.length === 0 ? (
                    <tr>
                      <td colSpan={totalColumns} className="text-center padding-lg text-muted">
                        {soloSinRegistrar
                          ? 'Todos los equipos tienen asistencia registrada'
                          : 'No hay equipos para mostrar'}
                      </td>
                    </tr>
                  ) : (
                    equiposPaginados.map(equipo => {
                      const { tieneAsistencia, isPending, selectValue } = getEquipoRowState(equipo.id);
                      return (
                        <tr key={equipo.id}
                            className={[
                              tieneAsistencia && !isPending ? 'asist-row-registered' : '',
                              selectedIds.has(equipo.id)    ? 'asist-row-selected'   : ''
                            ].filter(Boolean).join(' ')}>
                          {canMasivo && (
                            <td style={{ textAlign: 'center' }}>
                              <SelectCheckbox equipoId={equipo.id} selectedIds={selectedIds} onToggle={handleToggleSelect} />
                            </td>
                          )}
                          <td>
                            <span className="asist-equipo-codigo">{equipo.codigo}</span>
                          </td>
                          <td>
                            <div className="font-semibold">{equipo.marca} {equipo.modelo}</div>
                            {equipo.categoria?.nombre && (
                              <div className="text-muted text-sm">{equipo.categoria.nombre}</div>
                            )}
                          </td>
                          <td>
                            {canCreate ? (
                              <div className="asist-ubicacion-cell">
                                <select
                                  className={`asist-select-inline ${selectValue ? 'has-value' : ''} ${isPending ? 'pending-change' : ''}`}
                                  value={selectValue}
                                  onChange={(e) => handleSelectChange(equipo.id, e.target.value)}>
                                  <option value="">Sin asignar</option>
                                  {ubicacionOptions}
                                </select>
                                <button
                                  className={`asist-save-btn ${isPending ? 'asist-save-btn--pending' : tieneAsistencia ? 'asist-save-btn--saved' : ''}`}
                                  onClick={() => handleRegistroIndividual(equipo.id)}
                                  disabled={!isPending || savingIds.has(equipo.id)}
                                  title={isPending ? 'Guardar cambio' : 'Sin cambios pendientes'}>
                                  <i className={`fas ${isPending ? 'fa-save' : 'fa-check'}`}></i>
                                </button>
                              </div>
                            ) : (
                              getUbicacionDisplay(equipo.id)
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <AsistenciaBadge tieneAsistencia={tieneAsistencia} isPending={isPending} />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* CARDS MOBILE (visibles en ≤768px) */}
            <div className="asist-mobile-cards">
              {!loading && equiposPaginados.map(equipo => {
                const { tieneAsistencia, isPending, selectValue } = getEquipoRowState(equipo.id);
                const cardClass = [
                  'asist-mobile-card',
                  tieneAsistencia && !isPending ? 'registered' : '',
                  selectedIds.has(equipo.id)    ? 'selected'   : ''
                ].filter(Boolean).join(' ');
                const saveBtnClass = [
                  'asist-mobile-save-btn',
                  isPending      ? 'asist-mobile-save-btn--pending' : '',
                  tieneAsistencia && !isPending ? 'asist-mobile-save-btn--saved' : ''
                ].filter(Boolean).join(' ');
                return (
                  <div key={equipo.id} className={cardClass}>
                    <div className="mobile-card-header">
                      <div className="asist-mobile-card-header-left">
                        {canMasivo && (
                          <SelectCheckbox equipoId={equipo.id} selectedIds={selectedIds} onToggle={handleToggleSelect} />
                        )}
                        <span className="mobile-card-title">{equipo.codigo}</span>
                      </div>
                      <AsistenciaBadge tieneAsistencia={tieneAsistencia} isPending={isPending} />
                    </div>
                    <div className="asist-mobile-card-content">
                      <div className="asist-mobile-card-field">
                        <span className="asist-mobile-card-label">Equipo</span>
                        <span className="asist-mobile-card-value">{equipo.marca} {equipo.modelo}</span>
                      </div>
                      {equipo.categoria?.nombre && (
                        <div className="asist-mobile-card-field">
                          <span className="asist-mobile-card-label">Tipo</span>
                          <span className="asist-mobile-card-value text-muted">{equipo.categoria.nombre}</span>
                        </div>
                      )}
                      <div className="asist-mobile-card-field">
                        <span className="asist-mobile-card-label">Ubicación</span>
                        <span className="asist-mobile-card-value">{getUbicacionDisplay(equipo.id)}</span>
                      </div>
                    </div>
                    {canCreate && (
                      <div className="asist-mobile-card-footer">
                        <div className="asist-mobile-ubicacion-row">
                          <select className="form-select asist-mobile-ubicacion-select"
                                  value={selectValue}
                                  onChange={(e) => handleSelectChange(equipo.id, e.target.value)}>
                            <option value="">Sin asignar</option>
                            {ubicacionOptions}
                          </select>
                          <button className={saveBtnClass}
                                  onClick={() => handleRegistroIndividual(equipo.id)}
                                  disabled={!isPending || savingIds.has(equipo.id)}>
                            <i className={`fas ${isPending ? 'fa-save' : 'fa-check'}`}></i>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <Pagination
              currentPage={page}
              totalItems={totalPaginacion}
              itemsPerPage={limit}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleLimitChange}
            />
          </div>

          {/* ── SIDEBAR ──────────────────────────────────────────── */}
          {!soloSinRegistrar && (
            <div className="asist-sidebar-col">

              {/* Toggle: solo visible en tablet ≤1100px */}
              <button className={`asist-sidebar-toggle ${sidebarOpen ? 'open' : ''}`}
                      onClick={() => setSidebarOpen(p => !p)}>
                <span>
                  <i className="fas fa-map-marker-alt asist-sidebar-toggle-fa"></i>
                  Resumen por Faena — <strong className="asist-sidebar-toggle-cov">{coberturaPercent}%</strong> cobertura
                </span>
                <i className="fas fa-chevron-down asist-toggle-icon"></i>
              </button>

              {/* Panel */}
              <div className={`asist-sidebar-panel ${sidebarOpen ? 'open' : ''}`}>
                <div className="asist-sidebar-sticky">
                  <div className="card">

                    <div className="asist-sidebar-section-title">
                      <i className="fas fa-map-marker-alt"></i> Resumen por Faena
                    </div>

                    {/* Lista desktop */}
                    <div className="asist-faena-list">
                      {resumenUbicaciones.map(ub => {
                        const isActive = filtroUbicacionId === ub.ubicacion_id;
                        return (
                          <div
                            key={ub.ubicacion_id}
                            className={`asist-faena-item${isActive ? ' asist-faena-item--active' : ''}`}
                            onClick={() => handleFiltrarPorFaena(ub.ubicacion_id)}
                            title="Clic para filtrar equipos en esta faena"
                          >
                            <span className="asist-faena-code">{ub.codigo}</span>
                            <span className="asist-faena-name">{ub.nombre}</span>
                            <span className="asist-faena-count">{ub.cantidad_equipos}</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Vista chips: tablet */}
                    <div className="asist-faena-list-grid">
                      {resumenUbicaciones.map(ub => {
                        const isActive = filtroUbicacionId === ub.ubicacion_id;
                        return (
                          <span
                            key={ub.ubicacion_id}
                            className={`asist-faena-chip${isActive ? ' asist-faena-chip--active' : ''}`}
                            onClick={() => handleFiltrarPorFaena(ub.ubicacion_id)}
                          >
                            <span className="asist-faena-chip-code">{ub.codigo}</span>
                            {ub.nombre}
                            <span className="asist-faena-chip-count">{ub.cantidad_equipos}</span>
                          </span>
                        );
                      })}
                    </div>

                    <div className="asist-sidebar-footer">
                      <div className="asist-sidebar-stat">
                        <span>Con ubicación</span><strong>{registrados}</strong>
                      </div>
                      <div className="asist-sidebar-stat">
                        <span>Sin registrar</span><strong>{totalEquipos - registrados}</strong>
                      </div>
                      <hr className="asist-sidebar-divider" />
                      <div className="asist-sidebar-coverage">
                        <span>Cobertura</span><strong>{coberturaPercent}%</strong>
                      </div>
                    </div>

                  </div>
                </div>
              </div>

            </div>
          )}

        </div>
      </div>

      {/* Modal de exportación por rango de fechas */}
      <EntityModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onSave={handleExportarRango}
        title="Exportar Asistencias"
        fields={EXPORT_FIELDS}
        initialData={null}
        loading={exportLoading}
        submitLabel="Exportar"
        crossFieldValidator={(data) => {
          const errors = {};
          if (data.fecha_inicio && data.fecha_fin) {
            const r = validateDateRange(data.fecha_inicio, data.fecha_fin);
            if (!r.valid) errors.fecha_fin = r.message;
          }
          return errors;
        }}
      />
    </>
  );
}

export default AsistenciaEquipos;
